import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDiatonicChords, getChordQuality, insertChordAtCursor } from '../../utils/diatonicChords';
import { getChartInstrumentId } from '../../utils/midiInstruments';
import useChordAudition from '../../hooks/useChordAudition';
import PianoScalePopup from '../PianoScale/PianoScalePopup';
import ChordLibrarySheet from '../ChordLibrarySheet';
import Icon from '../common/Icon';

import { ReactComponent as LibraryIcon } from '../../icons/dots-grid3x3.svg';
import { ReactComponent as PianoIcon } from '../../icons/piano.svg';

import './ChordSuggestions.scss';

const ChordSuggestions = ({ currentKey, setInputToState }) => {
  const { t } = useTranslation();
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const closeLibrary = useCallback(() => setIsLibraryOpen(false), []);
  const [pianoAnchorRect, setPianoAnchorRect] = useState(null);
  const closePiano = useCallback(() => setPianoAnchorRect(null), []);
  // Only the library sounds; the voice is held while it is open
  const play = useChordAudition(getChartInstrumentId(), isLibraryOpen);

  // Get the diatonic chords for the current key
  const chords = getDiatonicChords(currentKey);

  // Handle chord button click
  const handleChordClick = (chordName) => {
    insertChordAtCursor(chordName, setInputToState);
  };

  // Closed first: inserting refocuses the textarea, which would raise the keyboard under the sheet
  const handleLibraryAdd = (chordName) => {
    setIsLibraryOpen(false);
    insertChordAtCursor(chordName, setInputToState);
  };

  const handlePianoClick = (e) => {
    setPianoAnchorRect(pianoAnchorRect ? null : e.currentTarget.getBoundingClientRect());
  };

  // Don't render if no key is selected or no chords generated
  if (!currentKey || chords.length === 0) {
    return null;
  }

  return (
    <div className="ChordSuggestions">
      <div className="buttons horizontal-menu">
        {chords.map((chord, index) => {
          const chordQuality = getChordQuality(chord.name);
          return (
            <button
              key={`${chord.name}-${index}`}
              className={`chord-btn ${chordQuality}`}
              type="button"
              title={`${chord.roman} - ${chord.name}`}
              onClick={() => handleChordClick(chord.name)}
            >
              {chord.name}
            </button>
          );
        })}
        <button
          type="button"
          className="chord-btn chord-btn--more"
          title={t('dialogs.chordBars.chordLibrary')}
          aria-label={t('dialogs.chordBars.chordLibrary')}
          onClick={() => setIsLibraryOpen(true)}
        >
          <Icon icon={<LibraryIcon />} />
        </button>
        {/* The popup closes on any press outside it; kept off this one so a second tap toggles it shut */}
        <button
          type="button"
          className="chord-btn chord-btn--more"
          title={t('dialogs.chordBars.scaleKeyboard')}
          aria-label={t('dialogs.chordBars.scaleKeyboard')}
          aria-expanded={!!pianoAnchorRect}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={handlePianoClick}
        >
          <Icon icon={<PianoIcon />} />
        </button>
      </div>
      <PianoScalePopup currentKey={currentKey} anchorRect={pianoAnchorRect} onClose={closePiano} />

      <ChordLibrarySheet
        songKey={currentKey}
        isOpen={isLibraryOpen}
        onClose={closeLibrary}
        onPlay={play}
        onAdd={handleLibraryAdd}
      />
    </div>
  );
};

export default ChordSuggestions;
